/**
 * Voice Diagnostics modal — the Developer Mode surface.
 *
 * Full-screen sheet hosting four tabs:
 *   - Library     : every voiceprint + drilldown into its samples
 *   - Adaptations : seed/adapt event feed (Phase 3g proof)
 *   - Sessions    : last N sessions with per-session rollups
 *   - Data        : row counts, disk stats, export, wipe
 *
 * Intentionally styled yellow/black-terminal so nothing here ever gets
 * confused with the normal Suron surface. If a screenshot of this leaks
 * into a design review, it reads as "debug tool" at a glance.
 */
import { useState } from "react";
import type { ComponentType, SVGProps } from "react";
import {
  Database,
  History,
  Radio,
  Sparkles,
  Users,
  X,
} from "lucide-react";

import {
  setDeveloperMode,
  useDeveloperMode,
} from "@/hooks/useDeveloperMode";

import { AdaptationsTab } from "./AdaptationsTab";
import { CatalogTab } from "./CatalogTab";
import { DataTab } from "./DataTab";
import { LibraryTab } from "./LibraryTab";
import { SessionsTab } from "./SessionsTab";

type DevTab = "library" | "catalog" | "adaptations" | "sessions" | "data";

interface VoiceDiagnosticsProps {
  onClose: () => void;
}

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const TABS: { id: DevTab; label: string; icon: IconComponent }[] = [
  { id: "library", label: "Library", icon: Users },
  { id: "catalog", label: "Catalog", icon: Radio },
  { id: "adaptations", label: "Adapt", icon: Sparkles },
  { id: "sessions", label: "Sessions", icon: History },
  { id: "data", label: "Data", icon: Database },
];

export function VoiceDiagnostics({ onClose }: VoiceDiagnosticsProps) {
  const [tab, setTab] = useState<DevTab>("adaptations");
  const overlaysOn = useDeveloperMode();

  return (
    <div className="fixed inset-0 z-[10000] flex flex-col bg-black text-white">
      <header
        className="flex items-center justify-between gap-3 border-b border-yellow-400/30 bg-black px-4"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
        }}
      >
        <div className="flex flex-col">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400/80">
            Developer mode
          </p>
          <h2 className="text-[18px] font-semibold text-white">
            Voice Diagnostics
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDeveloperMode(!overlaysOn)}
            aria-label="Toggle in-app dev overlays"
            className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest active:scale-95 ${
              overlaysOn
                ? "border-yellow-400/60 bg-yellow-400/10 text-yellow-300"
                : "border-white/15 bg-white/[0.03] text-white/50"
            }`}
          >
            overlays {overlaysOn ? "on" : "off"}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close diagnostics"
            className="rounded-full border border-white/10 p-2 text-white/70 active:scale-95"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <nav className="flex border-b border-white/10 bg-black">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex flex-1 flex-col items-center gap-1 py-2 transition-all ${
                active
                  ? "border-b-2 border-yellow-400 text-yellow-300"
                  : "border-b-2 border-transparent text-white/40 active:text-white/70"
              }`}
            >
              <Icon
                className={`h-[18px] w-[18px] ${active ? "" : "opacity-80"}`}
              />
              <span
                className={`text-[10px] font-medium ${
                  active ? "" : "tracking-tight"
                }`}
              >
                {t.label}
              </span>
            </button>
          );
        })}
      </nav>

      <div
        className="flex-1 overflow-y-auto"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        }}
      >
        {tab === "library" && <LibraryTab />}
        {tab === "catalog" && <CatalogTab />}
        {tab === "adaptations" && <AdaptationsTab />}
        {tab === "sessions" && <SessionsTab />}
        {tab === "data" && <DataTab />}
      </div>
    </div>
  );
}
